import { useEffect, useState, useCallback } from "react";
import { useTeacherTheme } from "@/contexts/TeacherThemeContext";
import { Star, Award, RefreshCw, Shield, Minus, TrendingUp, Medal } from "lucide-react";
import { dashboardService, type MonitoredStudent } from "@/services/dashboardService";
import { safeLog } from '@/utils/safeLog';

interface LeaderboardEntry {
  id: string;
  rank: number;
  name: string;
  safetyScore: number;   // 100 - riskScore
  riskScore: number;
  alertsThisMonth: number;
  initials: string;
}

export function Game() {
  const { dm } = useTeacherTheme();
  const [students, setStudents] = useState<MonitoredStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getMonitoredStudents(1, 50);
      setStudents(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      safeLog.error("Failed to load gamification data", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Build safety leaderboard: highest safety score (100 - riskScore) = safest student
  const leaderboard: LeaderboardEntry[] = [...students]
    .map(s => ({
      id: s.id,
      name: s.name,
      safetyScore: Math.max(0, 100 - s.riskScore),
      riskScore: s.riskScore,
      alertsThisMonth: s.alertsThisMonth ?? 0,
      initials: s.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2),
      rank: 0,
    }))
    .sort((a, b) => b.safetyScore - a.safetyScore)
    .map((s, i) => ({ ...s, rank: i + 1 }));

  const champion = leaderboard[0];
  const topStudents = leaderboard.slice(0, 10);

  const classAvgSafety = leaderboard.length > 0
    ? Math.round(leaderboard.reduce((s, e) => s + e.safetyScore, 0) / leaderboard.length)
    : 0;

  const starColor = (rank: number) => {
    if (rank === 1) return "text-yellow-400";
    if (rank === 2) return "text-slate-400";
    if (rank === 3) return "text-amber-600";
    return "text-slate-300";
  };

  const barColor = (riskScore: number) => {
    if (riskScore >= 70) return { filled: "bg-emerald-500", empty: "bg-emerald-100" };
    if (riskScore >= 40) return { filled: "bg-amber-400", empty: "bg-amber-100" };
    return { filled: "bg-emerald-500", empty: "bg-emerald-100" };
  };

  const trendIcon = (riskScore: number) => {
    if (riskScore >= 70) return <TrendingUp className="h-4 w-4 text-emerald-500" />;
    if (riskScore >= 40) return <Minus className="h-4 w-4 text-amber-400" />;
    return <TrendingUp className="h-4 w-4 text-emerald-500" />;
  };

  const getMedalIcon = (rank: number) => {
    if (rank === 1) return <Medal className="h-5 w-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-slate-400" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />;
    return null;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-xl font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Safety Leaderboard</h2>
          <p className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'} mt-0.5`}>
            Students ranked by safety score (100 − risk score)
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="btn-glass btn-glass-pill flex items-center gap-2"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Refresh'}
        </button>
      </div>

      {loading && students.length === 0 ? (
        <div className={`flex justify-center items-center py-24 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
          <RefreshCw className={`animate-spin h-6 w-6 mr-2 ${dm ? 'text-slate-400' : ''}`} />
          <span className="text-sm">Loading leaderboard...</span>
        </div>
      ) : students.length === 0 ? (
        <div className={`flex flex-col items-center py-20 ${dm ? 'text-slate-500' : 'text-slate-400'} gap-3`}>
          <Shield className={`h-10 w-10 ${dm ? 'text-slate-600' : 'text-slate-300'}`} />
          <p className="text-sm">No students found for your school.</p>
        </div>
      ) : (
        <>
          {/* Champion banner */}
          {champion && (
            <div className={`border rounded-2xl p-6 flex items-center gap-5 shadow-sm ${dm ? 'bg-gradient-to-r from-yellow-900/30 to-amber-900/30 border-yellow-700/50' : 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200'}`}>
              <div className={`rounded-full p-3 ${dm ? 'bg-yellow-900/50' : 'bg-yellow-100'}`}>
                <Award className={`h-8 w-8 ${dm ? 'text-yellow-400' : 'text-yellow-500'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${dm ? 'text-yellow-400' : 'text-yellow-700'}`}>Weekly Safety Champion</p>
                <h3 className={`text-lg font-bold truncate ${dm ? 'text-slate-100' : 'text-slate-800'}`}>{champion.name}</h3>
                <p className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
                  Safety score: <span className={`font-bold ${dm ? 'text-emerald-400' : 'text-emerald-600'}`}>{champion.safetyScore}</span> / 100
                  {champion.alertsThisMonth === 0 && ' · Zero alerts this month '}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className={`text-3xl font-black ${dm ? 'text-yellow-400' : 'text-yellow-500'}`}>#{champion.rank}</p>
                <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>Top student</p>
              </div>
            </div>
          )}

          {/* Class average */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className={`border rounded-xl p-4 shadow-sm text-center ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
              <p className={`text-2xl font-bold ${dm ? 'text-emerald-400' : 'text-emerald-600'}`}>{classAvgSafety}</p>
              <p className={`text-xs mt-0.5 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Class avg safety score</p>
            </div>
            <div className={`border rounded-xl p-4 shadow-sm text-center ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
              <p className={`text-2xl font-bold ${dm ? 'text-blue-400' : 'text-blue-600'}`}>{leaderboard.length}</p>
              <p className={`text-xs mt-0.5 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Students monitored</p>
            </div>
            <div className={`border rounded-xl p-4 shadow-sm text-center col-span-2 sm:col-span-1 ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
              <p className={`text-2xl font-bold ${dm ? 'text-slate-200' : 'text-slate-700'}`}>
                {leaderboard.filter(s => s.riskScore < 40).length}
              </p>
              <p className={`text-xs mt-0.5 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Low-risk students</p>
            </div>
          </div>

          {/* Leaderboard */}
          <div className={`border rounded-2xl shadow-sm overflow-hidden ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className={`px-6 py-4 border-b flex items-center gap-2 ${dm ? 'border-slate-700' : 'border-slate-100'}`}>
              <Star className={`h-4 w-4 ${dm ? 'text-slate-500' : 'text-slate-400'}`} />
              <h3 className={`text-sm font-semibold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>
                Top {topStudents.length} Students
              </h3>
            </div>
            <div className={`divide-y ${dm ? 'divide-slate-700' : 'divide-slate-100'}`}>
              {topStudents.map(entry => {
                const colors = barColor(entry.riskScore);
                return (
                  <div
                    key={entry.id}
                    className={`flex items-center gap-4 px-6 py-4 transition ${dm ? 'hover:bg-slate-700/60' : 'hover:bg-slate-50'}`}
                  >
                    {/* Rank */}
                    <div className="flex flex-col items-center shrink-0 w-8">
                      <Star className={`h-5 w-5 ${starColor(entry.rank)}`} />
                      <span className={`text-xs font-bold mt-0.5 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>#{entry.rank}</span>
                    </div>

                    {/* Avatar */}
                    <div className={`flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold ${
                      entry.riskScore < 40 ? (dm ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-100 text-emerald-700') :
                      entry.riskScore < 70 ? (dm ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700') :
                      (dm ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-700')
                    }`}>
                      {entry.initials}
                    </div>

                    {/* Name + bar */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          {getMedalIcon(entry.rank)}
                          <p className={`text-sm font-semibold truncate ${dm ? 'text-slate-100' : 'text-slate-800'}`}>{entry.name}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                          {trendIcon(entry.riskScore)}
                          <span className={`text-sm font-bold ${
                            entry.riskScore >= 70 ? 'text-emerald-600' :
                            entry.riskScore >= 40 ? 'text-amber-600' : 'text-emerald-600'
                          }`}>{entry.safetyScore} pts</span>
                        </div>
                      </div>
                      <div className={`w-full rounded-full h-2 overflow-hidden ${dm ? 'bg-slate-700' : 'bg-gray-100'}`}>
                        <div
                          className={`h-full ${colors.filled} rounded-full transition-all`}
                          style={{ width: `${entry.safetyScore}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {leaderboard.length > 10 && (
              <div className={`px-6 py-3 border-t text-center ${dm ? 'bg-slate-700/50 border-slate-700' : 'bg-slate-50 border-slate-100'}`}>
                <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
                  Showing top 10 of {leaderboard.length} students
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
